import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ITask } from '../../types/task';
import { TASK_GROUP_TO_EXPERIMENT_TYPE, ExperimentType } from '../../types/experiment';
import './TaskEditModal.css';

interface ExperimentOption {
    _id: string;
    experiment_id: string;
    name: string;
    provider?: string;
    model?: string;
    type?: ExperimentType;
    created: string;
    processedDocs: number;
    validationRate?: number;
}

interface TaskEditModalProps {
    task: ITask;
    isOpen: boolean;
    onClose: () => void;
    onSave: (updatedTask: Partial<ITask>) => Promise<void>;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3369/staging/api/v1';

const TaskEditModal: React.FC<TaskEditModalProps> = ({ task, isOpen, onClose, onSave }) => {
    const { t } = useTranslation();

    // Form state
    const [description, setDescription] = useState(task.description || '');
    const [batchSize, setBatchSize] = useState(task.batch_size);
    const [waitTime, setWaitTime] = useState(task.wait_time);
    const [provider, setProvider] = useState(task.provider || '');
    const [retryOnErrors, setRetryOnErrors] = useState(task.retry_on_errors);
    const [experimentId, setExperimentId] = useState(task.experiment_id || '');
    const [experimentName, setExperimentName] = useState(task.experiment_name || '');
    const [logsUrl, setLogsUrl] = useState(task.logs_url || '');
    const [pipelineOrder, setPipelineOrder] = useState(task.pipeline_order);

    // Options state
    const [providers, setProviders] = useState<string[]>([]);
    const [experiments, setExperiments] = useState<ExperimentOption[]>([]);
    const [experimentSearch, setExperimentSearch] = useState('');
    const [showExperimentDropdown, setShowExperimentDropdown] = useState(false);

    // UI state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    // Fetch providers based on task group
    const fetchProviders = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const group = task.group || 'DEFAULT';
            const response = await fetch(`${API_URL}/admin/tasks/providers/${group}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setProviders(data.providers || []);
            }
        } catch (err) {
            console.error('Error fetching providers:', err);
        }
    }, [task.group]);

    // Fetch experiments filtered by task group type
    const fetchExperiments = useCallback(async (search?: string) => {
        try {
            const token = localStorage.getItem('authToken');
            const url = new URL(`${API_URL}/admin/tasks/experiments`);
            if (search) url.searchParams.append('search', search);

            // Filter experiments by task group type
            const taskGroup = task.group?.toUpperCase();
            if (taskGroup && TASK_GROUP_TO_EXPERIMENT_TYPE[taskGroup]) {
                url.searchParams.append('taskGroup', taskGroup);
            }
            // Only show active experiments by default
            url.searchParams.append('activeOnly', 'true');

            const response = await fetch(url.toString(), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setExperiments(data.experiments || []);
            }
        } catch (err) {
            console.error('Error fetching experiments:', err);
        }
    }, [task.group]);

    // Load data when modal opens
    useEffect(() => {
        if (isOpen) {
            setLoading(true);
            Promise.all([fetchProviders(), fetchExperiments()])
                .finally(() => setLoading(false));

            // Reset form to task values
            setDescription(task.description || '');
            setBatchSize(task.batch_size);
            setWaitTime(task.wait_time);
            setProvider(task.provider || '');
            setRetryOnErrors(task.retry_on_errors);
            setExperimentId(task.experiment_id || '');
            setExperimentName(task.experiment_name || '');
            setLogsUrl(task.logs_url || '');
            setPipelineOrder(task.pipeline_order);
            setError(null);
        }
    }, [isOpen, task, fetchProviders, fetchExperiments]);

    // Search experiments with debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            if (experimentSearch) {
                fetchExperiments(experimentSearch);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [experimentSearch, fetchExperiments]);

    const handleExperimentSelect = (exp: ExperimentOption) => {
        setExperimentId(exp.experiment_id);
        setExperimentName(exp.name);
        setShowExperimentDropdown(false);
        setExperimentSearch('');
    };

    const handleClearExperiment = () => {
        setExperimentId('');
        setExperimentName('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);

        try {
            await onSave({
                description,
                batch_size: batchSize,
                wait_time: waitTime,
                provider,
                retry_on_errors: retryOnErrors,
                experiment_id: experimentId || undefined,
                experiment_name: experimentName || undefined,
                logs_url: logsUrl,
                pipeline_order: pipelineOrder
            });
            onClose();
        } catch (err: any) {
            setError(err.message || t('taskEdit.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="task-edit-modal-overlay" onClick={onClose}>
            <div className="task-edit-modal" onClick={e => e.stopPropagation()}>
                <div className="task-edit-modal-header">
                    <h2>{t('taskEdit.title')}: {task.name}</h2>
                    <button className="task-edit-modal-close" onClick={onClose}>
                        <span className="material-icons">close</span>
                    </button>
                </div>

                {loading ? (
                    <div className="task-edit-modal-loading">
                        <span className="material-icons rotating">sync</span>
                        <p>{t('taskEdit.loading')}</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="task-edit-modal-form">
                        {error && (
                            <div className="task-edit-modal-error">
                                <span className="material-icons">error</span>
                                {error}
                            </div>
                        )}

                        <div className="task-edit-form-section">
                            <h3>{t('taskEdit.basicSettings')}</h3>

                            <div className="task-edit-form-group">
                                <label htmlFor="description">{t('taskEdit.description')}</label>
                                <textarea
                                    id="description"
                                    value={description}
                                    onChange={e => setDescription(e.target.value)}
                                    rows={3}
                                    placeholder={t('taskEdit.descriptionPlaceholder')}
                                />
                            </div>

                            <div className="task-edit-form-row">
                                <div className="task-edit-form-group">
                                    <label htmlFor="provider">{t('taskEdit.provider')}</label>
                                    <select
                                        id="provider"
                                        value={provider}
                                        onChange={e => setProvider(e.target.value)}
                                    >
                                        <option value="">{t('taskEdit.selectProvider')}</option>
                                        {providers.map(p => (
                                            <option key={p} value={p}>{p}</option>
                                        ))}
                                        {provider && !providers.includes(provider) && (
                                            <option value={provider}>{provider} ({t('taskEdit.current')})</option>
                                        )}
                                    </select>
                                    <span className="task-edit-form-hint">
                                        {t('taskEdit.providerHint', { group: task.group || 'DEFAULT' })}
                                    </span>
                                </div>

                                <div className="task-edit-form-group">
                                    <label htmlFor="pipelineOrder">{t('taskEdit.pipelineOrder')}</label>
                                    <input
                                        type="number"
                                        id="pipelineOrder"
                                        value={pipelineOrder}
                                        onChange={e => setPipelineOrder(Number(e.target.value))}
                                        min={0}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="task-edit-form-section">
                            <h3>{t('taskEdit.processingSettings')}</h3>

                            <div className="task-edit-form-row">
                                <div className="task-edit-form-group">
                                    <label htmlFor="batchSize">{t('taskEdit.batchSize')}</label>
                                    <input
                                        type="number"
                                        id="batchSize"
                                        value={batchSize}
                                        onChange={e => setBatchSize(Number(e.target.value))}
                                        min={1}
                                        max={10000}
                                    />
                                    <span className="task-edit-form-hint">{t('taskEdit.batchSizeHint')}</span>
                                </div>

                                <div className="task-edit-form-group">
                                    <label htmlFor="waitTime">{t('taskEdit.waitTime')}</label>
                                    <input
                                        type="number"
                                        id="waitTime"
                                        value={waitTime}
                                        onChange={e => setWaitTime(Number(e.target.value))}
                                        min={0}
                                    />
                                    <span className="task-edit-form-hint">{t('taskEdit.waitTimeHint')}</span>
                                </div>
                            </div>

                            <div className="task-edit-form-group task-edit-checkbox">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={retryOnErrors}
                                        onChange={e => setRetryOnErrors(e.target.checked)}
                                    />
                                    <span>{t('taskEdit.retryOnErrors')}</span>
                                </label>
                                <span className="task-edit-form-hint">{t('taskEdit.retryOnErrorsHint')}</span>
                            </div>
                        </div>

                        <div className="task-edit-form-section">
                            <h3>{t('taskEdit.experimentSettings')}</h3>

                            <div className="task-edit-form-group">
                                <label>{t('taskEdit.linkedExperiment')}</label>

                                {experimentId ? (
                                    <div className="task-edit-experiment-selected">
                                        <div className="task-edit-experiment-info">
                                            <span className="material-icons">science</span>
                                            <div>
                                                <strong>{experimentName || experimentId}</strong>
                                                <span className="task-edit-experiment-id">{experimentId}</span>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="task-edit-experiment-clear"
                                            onClick={handleClearExperiment}
                                        >
                                            <span className="material-icons">close</span>
                                        </button>
                                    </div>
                                ) : (
                                    <div className="task-edit-experiment-search">
                                        <input
                                            type="text"
                                            placeholder={t('taskEdit.searchExperiments')}
                                            value={experimentSearch}
                                            onChange={e => {
                                                setExperimentSearch(e.target.value);
                                                setShowExperimentDropdown(true);
                                            }}
                                            onFocus={() => {
                                                setShowExperimentDropdown(true);
                                                if (!experimentSearch) fetchExperiments();
                                            }}
                                        />
                                        <span className="material-icons">search</span>

                                        {showExperimentDropdown && experiments.length > 0 && (
                                            <div className="task-edit-experiment-dropdown">
                                                {experiments.map(exp => (
                                                    <div
                                                        key={exp._id}
                                                        className="task-edit-experiment-option"
                                                        onClick={() => handleExperimentSelect(exp)}
                                                    >
                                                        <div className="task-edit-experiment-option-main">
                                                            <strong>{exp.name}</strong>
                                                            <span className="task-edit-experiment-option-id">{exp.experiment_id}</span>
                                                            {exp.type && (
                                                                <span className="task-edit-experiment-type-badge">{exp.type}</span>
                                                            )}
                                                        </div>
                                                        <div className="task-edit-experiment-option-meta">
                                                            {exp.provider && <span>{exp.provider}</span>}
                                                            {exp.model && <span>{exp.model}</span>}
                                                            <span>{exp.processedDocs} docs</span>
                                                            {exp.validationRate !== undefined && (
                                                                <span>{(exp.validationRate * 100).toFixed(0)}% valid</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="task-edit-form-section">
                            <h3>{t('taskEdit.advancedSettings')}</h3>

                            <div className="task-edit-form-group">
                                <label htmlFor="logsUrl">{t('taskEdit.logsUrl')}</label>
                                <input
                                    type="url"
                                    id="logsUrl"
                                    value={logsUrl}
                                    onChange={e => setLogsUrl(e.target.value)}
                                    placeholder={t('taskEdit.logsUrlPlaceholder')}
                                />
                            </div>
                        </div>

                        <div className="task-edit-modal-actions">
                            <button
                                type="button"
                                className="task-edit-cancel-btn"
                                onClick={onClose}
                                disabled={saving}
                            >
                                {t('taskEdit.cancel')}
                            </button>
                            <button
                                type="submit"
                                className="task-edit-save-btn"
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <span className="material-icons rotating">sync</span>
                                        {t('taskEdit.saving')}
                                    </>
                                ) : (
                                    <>
                                        <span className="material-icons">save</span>
                                        {t('taskEdit.save')}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default TaskEditModal;
